import React, { useContext, useState, useEffect } from "react";
import {
  Menu,
  User,
  LogOut,
  ChevronDown,
  AlertTriangle,
  FilterIcon,
  ImageIcon,
  TrendingUp,
  DollarSign,
  Package,
  CreditCard,
  Percent,
  Truck,
  Receipt,
  Info,
  InfoIcon,
} from "lucide-react";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { LoadingFallback } from "@/components/loading-fallback";
import { cn, getDateRange } from "@/lib/utils";
import { MyAccountDropdown } from "@/components/my-account-dropdown";
import { useNavigate } from "react-router";
import { CurrencySelect } from "@/components/currency-select";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { useProductListBySales } from "@/hooks/product.hook";
import { ChartTileOverview } from "@/components/chart-tile";
import { ChartSettingsPopover } from "../../../../components/chart-settings-popover";
import { SelectDateRange } from "@/components/select-date-range-component";
import { useLiteProductStatistics } from "@/hooks/tiktokOrder.hooks";
import { LiteStatisticPeriod } from "@/types/LiteStatisticPeriod";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { LiteProductStatistic } from "@/types/LiteProductStatistic";
import moment from "moment";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

const fieldMappings: any = {
  netUnits: {
    header: "Net Units Sold",
    color: "#9333EA",
    icon: Truck,
    order: 0.5,
    isNotCurrency: true,
  },
  // totalGrossSales: {
  //   header: "Gross Sales",
  //   color: "#10B981",
  //   icon: DollarSign,
  //   order: 1,
  // },
  totalMerchandiseSales: {
    header: "Net Sales",
    color: "#3B82F6",
    icon: DollarSign,
    order: 2,
  },
  netProfits: {
    header: "Net Profits",
    color: "#14B8A6",
    icon: TrendingUp,
    order: 3,
  },
  costOfGoods: {
    header: "Cost of Goods",
    color: "#8B5CF6",
    icon: Package,
    order: 4,
  },
  totalTax: { header: "Sales VAT", color: "#EF4444", icon: Receipt, order: 5 },
  totalEstimatedAffiliateCommission: {
    header: "Affiliates",
    color: "#EC4899",
    icon: Percent,
    order: 6,
  },
  totalPlatformFee: {
    header: "TikTok Commiss. Fee",
    color: "#F97316",
    icon: CreditCard,
    order: 7,
  },
  totalEstimatedFbmShippingCost: {
    header: "FBM Shipping Cost",
    color: "#3B82F6",
    icon: Truck,
    order: 8,
  },
  totalEstimatedFbtFulfilmentAndPackagingFees: {
    header: "FBT Fulfill. & Packaging",
    color: "#3B82F6",
    icon: Truck,
    order: 9,
  },
  totalEstimatedFbtShippingCost: {
    header: "FBT Shipping Cost",
    color: "#3B82F6",
    icon: Truck,
    order: 10,
  },
  totalEstimatedSellerShippingCost: {
    header: "Seller-Fulfilled Shipping Cost",
    color: "#3B82F6",
    icon: Truck,
    order: 11,
  },
};

export const ProductStatistics = () => {
  const navigate = useNavigate();
  const { isSidebarOpen, setIsSidebarOpen, currency, swapChartsOnDashboard } =
    useContext(UserStateContext);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [selectedDateRange, setSelectedDateRange] = useState("30");
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());
  const [displayStyle, setDisplayStyle] = useState<"charts" | "time">("charts");
  const [selectedTimeUnit, setSelectedTimeUnit] = useState("month");
  const [selectedField, setSelectedField] = useState<keyof LiteStatisticPeriod>(
    "totalMerchandiseSales"
  );
  // Add memoized time columns to prevent re-renders
  const [timeColumns, setTimeColumns] = useState<{
    date: moment.Moment;
    formatted: string;
  }[]>([]);

  const {
    data: products,
    isLoading,
    error,
  } = useProductListBySales(fromDate, toDate, currentPage, pageSize);
  const {
    data: liteProducts,
    isLoading: isLiteLoading,
    error: liteError,
  } = useLiteProductStatistics(
    fromDate,
    toDate,
    products?.items.map((product) => product.productInfo.id) || []
  );

  const [selectedFields, setSelectedFields] = useState<
    (keyof LiteStatisticPeriod)[]
  >([
    "totalMerchandiseSales",
    "netProfits",
    "costOfGoods",
    "totalEstimatedAffiliateCommission",
    "netUnits",
  ]);

  const getAvailableTimeUnits = () => {
    const diffInDays = moment(toDate).diff(moment(fromDate), 'days');
    
    if (diffInDays <= 30) {
      return [{ value: "week", label: "Week by Week" }];
    } else if (diffInDays <= 60) {
      return [
        { value: "week", label: "Week by Week" },
        { value: "month", label: "Month by Month" }
      ];
    } else if (diffInDays <= 90) {
      return [{ value: "month", label: "Month by Month" }];
    } else if (diffInDays <= 180) {
      return [
        { value: "month", label: "Month by Month" },
        { value: "quarter", label: "Quarter by Quarter" }
      ];
    } else {
      return [
        { value: "month", label: "Month by Month" },
        { value: "quarter", label: "Quarter by Quarter" },
        { value: "year", label: "Year by Year" }
      ];
    }
  };

  useEffect(() => {
    const availableUnits = getAvailableTimeUnits();
    if (!availableUnits.find(unit => unit.value === selectedTimeUnit)) {
      setSelectedTimeUnit(availableUnits[0].value);
    }
  }, [fromDate, toDate]);

  // Update time columns when data or time unit changes
  useEffect(() => {
    if (liteProducts) {
      const allDates: { date: moment.Moment; formatted: string }[] = [];
      for (const productData of Object.values(liteProducts)) {
        for (const period of productData) {
          const date = moment(period.periodStart);
          let formatted = "";

          switch (selectedTimeUnit) {
            case "week": {
              // Get the year based on the week
              const weekYear = date.isoWeekYear();
              formatted = `Week ${date.isoWeek()} ${weekYear}`;
              break;
            }
            case "month":
              formatted = date.format("MMM YYYY");
              break;
            case "quarter":
              formatted = `Q${date.quarter()} ${date.year()}`;
              break;
            case "year":
              formatted = date.format("YYYY");
              break;
          }

          if (!allDates.some(d => d.formatted === formatted)) {
            allDates.push({ date, formatted });
          }
        }
      }

      const newColumns = allDates
        .sort((a, b) => {
          if (selectedTimeUnit === "week") {
            // For weeks, compare by ISO year first, then by ISO week
            const aYear = a.date.isoWeekYear();
            const bYear = b.date.isoWeekYear();
            if (aYear !== bYear) return aYear - bYear;
            return a.date.isoWeek() - b.date.isoWeek();
          }
          return a.date.valueOf() - b.date.valueOf();
        });

      setTimeColumns(newColumns);
    }
  }, [liteProducts, selectedTimeUnit]);

  const handleDateRangeChange = (fromDate: Date, toDate: Date) => {
    setFromDate(fromDate);
    setToDate(toDate);
    setSelectedDateRange("custom");
  };

  const handleRowClick = (productId: string) => {
    navigate(`/analytics/products/${productId}`);
  };

  const getProductSalesData = (productId: string): LiteStatisticPeriod[] => {
    return liteProducts && liteProducts[productId]
      ? liteProducts[productId]
      : [];
  };

  const getAggregatedProductData = (
    productId: string,
    dataKey: keyof LiteStatisticPeriod
  ): number => {
    if (!liteProducts || !liteProducts[productId]) {
      return 0;
    }

    return liteProducts[productId].reduce((sum, period) => {
      const value = period[dataKey];
      return sum + (typeof value === "number" ? value : 0);
    }, 0);
  };

  const getTimeGroupedData = (productId: string): { [key: string]: number } => {
    if (!liteProducts || !liteProducts[productId]) {
      return {};
    }

    const groupedData: { [key: string]: number } = {};
    liteProducts[productId].forEach((period) => {
      const date = moment(period.periodStart);
      let key = "";

      switch (selectedTimeUnit) {
        case "week": {
          const weekYear = date.isoWeekYear();
          key = `Week ${date.isoWeek()} ${weekYear}`;
          break;
        }
        case "month":
          key = date.format("MMM YYYY");
          break;
        case "quarter":
          key = `Q${date.quarter()} ${date.year()}`;
          break;
        case "year":
          key = date.format("YYYY");
          break;
      }

      if (!groupedData[key]) {
        groupedData[key] = 0;
      }
      groupedData[key] += period[selectedField] as number;
    });

    return groupedData;
  };

  const isCurrentTimePeriod = (timeColumn: moment.Moment): boolean => {
    const now = moment();
    
    switch (selectedTimeUnit) {
      case "week":
        return now.isSame(timeColumn, 'isoWeek');
      case "month":
        return now.isSame(timeColumn, 'month');
      case "quarter":
        return now.isSame(timeColumn, 'quarter');
      case "year":
        return now.isSame(timeColumn, 'year');
      default:
        return false;
    }
  };

  const getPercentageChange = (currentValue: number, previousValue: number) => {
    if (previousValue === 0) return null;
    return ((currentValue - previousValue) / previousValue) * 100;
  };

  const toggleField = (field: keyof LiteStatisticPeriod) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const sortedSelectedFields = selectedFields.sort(
    (a, b) => fieldMappings[a].order - fieldMappings[b].order
  );

  const truncateProductName = (name: string, maxLength: number = 70) => {
    return name.length > maxLength
      ? name.substring(0, maxLength) + "..."
      : name;
  };

  const renderPagination = () => {
    return (
      products && (
        <Pagination className="flex justify-center">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
              />
            </PaginationItem>
            {(() => {
              const totalPages = products.totalPages;
              const currentPage = products.currentPage;
              let startPage = Math.max(currentPage - 1, 1);
              let endPage = Math.min(startPage + 2, totalPages);

              if (endPage - startPage < 2) {
                startPage = Math.max(endPage - 2, 1);
              }

              return Array.from(
                { length: endPage - startPage + 1 },
                (_, i) => startPage + i
              ).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    isActive={page === currentPage}
                    className={`rounded-full cursor-pointer ${
                      page === currentPage
                        ? "bg-blue-500/50"
                        : "bg-background hover:bg-muted/50"
                    } border transition-colors`}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ));
            })()}
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )
    );
  };

  return (
    <div className="min-h-screen flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 bg-background overflow-y-auto min-h-screen">
        <header className="border-b pt-6 pb-2 shadow-sm h-auto flex flex-col gap-4 px-4 md:px-6">
          <div className="flex items-center gap-4 w-full">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="md:hidden text-foreground"
            >
              <Menu size={24} />
            </button>
            <div className="max-w-3xl">
            <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-foreground">
                  Product Statistics
                </h2>
                <Badge className="px-2 py-0 bg-orange-500/20 text-orange-500 text-[12px] flex gap-2 items-center">
                  <InfoIcon className="w-3 h-3" />
                  Excludes Samples
                </Badge>
              </div>
              <div className="flex items-start gap-4">
                <Info className="h-4 w-4 text-blue-500 mt-4 shrink-0" />
                <div className="text-sm text-muted-foreground">
                  Product-level statistics do not include or account for ad spend. Visit the{" "}
                  <Link
                    className="text-blue-500 underline hover:text-blue-600"
                    to="/dashboard"
                  >
                    Sales & Profit
                  </Link>{" "}
                  dashboard to view overall ad spend and performance.
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-auto">
              <ChartSettingsPopover />
              <CurrencySelect />
              <MyAccountDropdown />
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-muted-foreground">
                  Date Range
                </label>
                <SelectDateRange
                  onDateRangeChange={handleDateRangeChange}
                  initialFromDate={fromDate}
                  initialToDate={toDate}
                  initialRange={selectedDateRange}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm text-muted-foreground">
                  View Mode
                </label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex gap-2 items-center"
                    >
                      {displayStyle === "charts" ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <Receipt className="w-4 h-4" />
                      )}
                      {displayStyle === "charts" ? "Metrics as Columns" : "Time Periods as Columns"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setDisplayStyle("charts")}>
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Metrics as Columns
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDisplayStyle("time")}>
                      <Receipt className="w-4 h-4 mr-2" />
                      Time Periods as Columns
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {displayStyle === "charts" ? (
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-muted-foreground">
                    Chart Metrics
                  </label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex gap-2 items-center"
                      >
                        <FilterIcon className="w-4 h-4" />
                        Selected Metrics ({selectedFields.length})
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      <DropdownMenuLabel>
                        Toggle Metrics to Display
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.entries(fieldMappings)
                        .sort(
                          ([, a], [, b]) => (a as any).order - (b as any).order
                        )
                        .map(([field, mapping]) => {
                          const typedMapping = mapping as {
                            header: string;
                            order: number;
                          };
                          return (
                            <DropdownMenuCheckboxItem
                              key={field}
                              onSelect={(e) => {
                                e.preventDefault();
                              }}
                              checked={selectedFields.includes(
                                field as keyof LiteStatisticPeriod
                              )}
                              onCheckedChange={() =>
                                toggleField(field as keyof LiteStatisticPeriod)
                              }
                            >
                              {typedMapping.header}
                            </DropdownMenuCheckboxItem>
                          );
                        })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-muted-foreground">
                      Metric
                    </label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex gap-2 items-center"
                        >
                          <ChevronDown className="w-4 h-4" />
                          {fieldMappings[selectedField].header}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {Object.entries(fieldMappings).map(
                          ([field, mapping]) => {
                            const Icon = (mapping as any).icon;
                            return (
                              <DropdownMenuItem
                                key={field}
                                onClick={() =>
                                  setSelectedField(
                                    field as keyof LiteStatisticPeriod
                                  )
                                }
                              >
                                <Icon className="w-4 h-4 mr-2" />
                                {(mapping as any).header}
                              </DropdownMenuItem>
                            );
                          }
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-muted-foreground">
                      Time Grouping
                    </label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex gap-2 items-center"
                        >
                          <ChevronDown className="w-4 h-4" />
                          {
                            getAvailableTimeUnits().find(
                              (opt) => opt.value === selectedTimeUnit
                            )?.label
                          }
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {getAvailableTimeUnits().map((option) => (
                          <DropdownMenuItem
                            key={option.value}
                            onClick={() => setSelectedTimeUnit(option.value)}
                          >
                            {option.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end">
              {renderPagination()}
            </div>
          </div>
        </header>

        <div className="mt-6 px-6">
          <LoadingFallback loading={isLoading}>
            {error ? (
              <div className="text-red-500 font-semibold">
                Error: {error.message}
              </div>
            ) : (
              <>
                <div className="bg-background rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[250px] min-w-[250px] max-w-[250px] font-semibold text-foreground/70">
                          Name
                        </TableHead>
                        {displayStyle === "charts"
                          ? sortedSelectedFields.map((field) => (
                              <TableHead
                                key={field}
                                className="font-semibold text-foreground/70"
                              >
                                {fieldMappings[field].header}
                              </TableHead>
                            ))
                          : timeColumns.map((timeColumn) => (
                              <TableHead
                                key={timeColumn.formatted}
                                className={`w-[150px] min-w-[150px] max-w-[150px] font-semibold text-foreground/70 ${isCurrentTimePeriod(timeColumn.date) ? 'bg-yellow-500/10' : ''}`}
                              >
                                {timeColumn.formatted}
                              </TableHead>
                            ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products &&
                      products.items &&
                      products.items.length > 0 ? (
                        products.items.map((product: any) => (
                          <TableRow
                            key={product.productInfo.id}
                            onClick={() =>
                              handleRowClick(product.productInfo.id)
                            }
                            className="hover:bg-muted/50 transition-colors cursor-pointer"
                          >
                            <TableCell className="py-4">
                              <div className="flex items-center space-x-4">
                                {product.productInfo.mainImageUrl ? (
                                  <img
                                    src={product.productInfo.mainImageUrl}
                                    alt={product.productInfo.title}
                                    className="w-16 h-16 object-cover rounded-lg shadow-sm"
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-muted rounded-lg shadow-sm flex items-center justify-center">
                                    <ImageIcon className="w-8 h-8 text-gray-400" />
                                  </div>
                                )}
                                <div className="w-[250px] break-all whitespace-normal">
                                  <h3 className="font-medium text-foreground">
                                    {truncateProductName(
                                      product.productInfo.title
                                    )}
                                  </h3>
                                  <p className="text-sm text-foreground/60">
                                    {product.productInfo.brandName}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            {displayStyle === "charts"
                              ? sortedSelectedFields.map((field) => (
                                  <TableCell key={field} className="px-1 py-2">
                                    <ChartTileOverview
                                      tile={{
                                        value: fieldMappings[field]
                                          .isNotCurrency
                                          ? getAggregatedProductData(
                                              product.productInfo.id,
                                              field
                                            ).toLocaleString(undefined, {
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            })
                                          : `${
                                              currency.symbol
                                            }${getAggregatedProductData(
                                              product.productInfo.id,
                                              field
                                            ).toLocaleString(undefined, {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}`,
                                        icon: fieldMappings[field].icon,
                                        color: `from-${fieldMappings[
                                          field
                                        ].color.slice(
                                          1
                                        )}-400 to-${fieldMappings[
                                          field
                                        ].color.slice(1)}-600`,
                                        chartColor: fieldMappings[field].color,
                                        format: "currency",
                                        dataKey: field,
                                        isNotCurrency:
                                          fieldMappings[field].isNotCurrency,
                                        dataLabel: "LIVE",
                                        gridClass: "col-span-2",
                                        heightClass: "h-[150px]",
                                      }}
                                      isLoading={isLiteLoading}
                                      isSettledLoading={false}
                                      combinedData={getProductSalesData(
                                        product.productInfo.id
                                      )}
                                    />
                                  </TableCell>
                                ))
                              : timeColumns.map((timeColumn, index) => {
                                  const currentValue = getTimeGroupedData(product.productInfo.id)[timeColumn.formatted] || 0;
                                  const previousValue = index > 0 
                                    ? getTimeGroupedData(product.productInfo.id)[timeColumns[index - 1].formatted] || 0
                                    : null;
                                  const percentageChange = previousValue !== null 
                                    ? getPercentageChange(currentValue, previousValue)
                                    : null;
                                    
                                  return (
                                    <TableCell 
                                      key={timeColumn.formatted} 
                                      className={`py-2 ${isCurrentTimePeriod(timeColumn.date) ? 'bg-yellow-500/10' : ''}`}
                                    >
                                      <div className="flex flex-col">
                                        <span>
                                          {fieldMappings[selectedField].isNotCurrency
                                            ? currentValue.toLocaleString(undefined, {
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0,
                                              })
                                            : `${currency.symbol}${currentValue.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              })}`}
                                        </span>
                                        {percentageChange !== null && (
                                          <div className={`text-xs flex items-center gap-0.5 ${percentageChange > 0 ? 'text-green-500' : percentageChange < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                                            <span>{percentageChange > 0 ? '↑' : percentageChange < 0 ? '↓' : '→'}</span>
                                            <span>{Math.abs(percentageChange).toFixed(1)}%</span>
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                  );
                                })}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={
                              displayStyle === "charts"
                                ? sortedSelectedFields.length + 1
                                : timeColumns.length + 1
                            }
                            className="text-center py-8 text-foreground/60"
                          >
                            No products found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
            <div className="my-8">{renderPagination()}</div>
          </LoadingFallback>
        </div>
      </main>
    </div>
  );
};
